// Typed client for Heimar.Api.
//
// Same-origin by design: the bundle is served out of Heimar.Api/wwwroot, so
// these are plain relative paths — no base URL, no CORS, no BFF hop. In dev,
// Vite proxies /api and /packs to the ASP.NET host (see vite.config.ts).

import { accessToken } from './supabase'
import type {
  AccountRow,
  AdvanceRequest,
  AdvanceResponse,
  AffordancesResponse,
  MessagesResponse,
  OpeningResponse,
  PackListResponse,
  SessionState,
  StartSessionRequest,
  StartSessionResponse,
  StatementResponse,
  Sitting,
  ThreadsResponse,
  TranscriptLine,
  TranscriptPack,
  WalletQuote,
  WalletStanding,
} from './contract'
import type { Account } from './auth'

/** Thrown for any non-2xx response, carrying the status for callers that care. */
export class ApiError extends Error {
  readonly status: number

  constructor(status: number, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // Every call carries the Supabase access token. The server validates it and
  // reads the account's status itself — the client's opinion is never trusted.
  const token = await accessToken()

  const res = await fetch(path, {
    ...init,
    headers: {
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers,
    },
  })

  if (!res.ok) {
    // 401 = no account; 403 = an account that has not been let in yet.
    const body = await res.text().catch(() => '')
    throw new ApiError(res.status, body || res.statusText)
  }

  if (res.status === 204) return undefined as T
  const text = await res.text()
  return (text ? JSON.parse(text) : undefined) as T
}

/**
 * A PLAYER ACT ON A SESSION THE SERVER NO LONGER HOLDS — retry it once, through a revive.
 *
 * Sessions live in memory (`SessionRegistry`); the disk copy is what survives. So a server
 * restart — every deploy — leaves every open tab holding an id that 404s on the next thing the
 * player does, and the surface has no way back: the open effect is guarded against re-running, so
 * the thread simply shows an error until a hard reload. Reported as the "first revive after
 * restart" family (`docs/specs/2026-08-27-the-wallet.md` §6.16), where it is also the
 * **paid-for-a-session-that-won't-open** case the day the wallet attaches.
 *
 * Safe to do blindly only because `StartSession` now persists at creation: a session id a client
 * holds always has a file behind it, so a revive either works or the thread genuinely does not
 * exist — in which case the second attempt raises the same 404 and the caller sees the truth.
 *
 * ⚠ THE HEARTBEAT MUST NEVER RESURRECT A SESSION — only the player's own act may. `activity`
 * deliberately does NOT come through here: a tab left open on an abandoned thread would otherwise
 * revive it every 45 seconds forever, which is a session nobody is reading that is still holding
 * memory, still able to spend, and (once the wallet lands) still holding a booking.
 */
async function throughRevive<T>(id: string, act: () => Promise<T>): Promise<T> {
  try {
    return await act()
  } catch (e) {
    if (!(e instanceof ApiError) || e.status !== 404) throw e
    await post(`/api/session/${id}/revive`)
    return await act()
  }
}

const post = <T>(path: string, body?: unknown) =>
  request<T>(path, {
    method: 'POST',
    body: body === undefined ? undefined : JSON.stringify(body),
  })

export const api = {
  // --- session lifecycle -------------------------------------------------
  startSession: (req: StartSessionRequest) =>
    post<StartSessionResponse>('/api/session/start', req),

  getState: (id: string) => request<SessionState>(`/api/session/${id}/state`),

  /**
   * The opening run (brief §6.4). Called ONCE at desk mount, create and resume
   * alike — `complete` is the server's own skip rule, so the client never has to
   * remember whether this session has been played.
   */
  getOpening: (id: string) => request<OpeningResponse>(`/api/session/${id}/opening`),

  listResumable: () => request<unknown[]>('/api/session/resumable'),

  /**
   * The guest's doors for a pack — the corridor's one read, and also the resume path on a
   * one-person pack (which comes back as a single door). Scoped to the caller server-side.
   */
  getThreads: (packId: string) =>
    request<ThreadsResponse>(`/api/session/threads?packId=${encodeURIComponent(packId)}`),

  revive: (id: string) => post<SessionState>(`/api/session/${id}/revive`),

  // --- the wallet --------------------------------------------------------
  // Threshold and statement only. There is no "current spend" read, and there
  // must never be one: a number a surface can poll during play is a meter.
  wallet: () => request<WalletStanding>('/api/wallet'),

  quote: (packId: string) =>
    request<WalletQuote>(`/api/wallet/quote?packId=${encodeURIComponent(packId)}`),

  statement: (limit = 100) =>
    request<StatementResponse>(`/api/wallet/statement?limit=${limit}`),

  // --- the observer, dev only ---------------------------------------------
  // These endpoints exist only in Development (Program.cs, the dev block) and the only route that
  // calls them is a dev harness; the types live beside the harness, not in the contract mirror.
  devObserverIndex: () => request<unknown>(`/api/dev/observer`),
  devObserver: (sessionId: string) => request<unknown>(`/api/dev/observer/${sessionId}`),
  devObserverReadings: (sessionId: string) => request<unknown>(`/api/dev/observer/${sessionId}/readings`),
  devObserverReplay: (sessionId: string, apply = false) =>
    post<unknown>(`/api/dev/observer/${sessionId}/replay?confirm=true&apply=${apply}`, {}),

  /** ⚠ Stripe's placeholder — an admin grant, until cards are wired. `minor` = cents, oere, yen. */
  topUp: (accountId: string, minor: number, currency = 'USD', note?: string) =>
    post<{ id: string }>('/api/admin/wallet/topup', { accountId, minor, currency, note }),

  /** Price a TIER in one currency. Adding a market is one call per tier, not one per pack. */
  setTier: (tier: {
    tierId: string
    currency: string
    basePriceMicros: number
    unit: string
    unitPriceMicros: number
    includedUnits: number
    maxUnits: number | null
    note?: string
  }) => post<{ id: string; priceMinor: number }>('/api/admin/wallet/tier', tier),

  /** Put a door on a tier. The only pricing decision a new pack needs: a kind, not a number. */
  setSkuTier: (skuId: string, tierId: string, note?: string) =>
    post<{ id: string }>('/api/admin/wallet/sku', { skuId, tierId, note }),

  prices: () => request<{ tiers: unknown[]; skus: unknown[] }>('/api/admin/wallet/prices'),

  // --- the will ----------------------------------------------------------
  /** Player input: the will at the threshold. Returns 202 — the feed carries the result. */
  sendInput: (id: string, input: string) =>
    throughRevive(id, () => post<void>(`/api/session/${id}/input`, { input })),

  /** A message on a channel (Messaging / Correspondent formats). */
  sendMessage: (id: string, content: string) =>
    throughRevive(id, () => post<void>(`/api/session/${id}/message`, { content })),

  /**
   * The standing intent — goal + constraints in the player's own language.
   * Null/blank sets it aside (idle), which under `continuationRequiresIntent`
   * stops continuations firing and costing.
   */
  setIntent: (id: string, text: string | null) =>
    post<{ standingIntent: string | null }>(`/api/session/${id}/intent`, { text }),

  /** Ask-refresh: re-read the situation without acting. */
  ask: (id: string, question: string) =>
    post<unknown>(`/api/session/${id}/ask`, { question }),

  // --- clock -------------------------------------------------------------
  /** The World pass: resolve a skipped interval and commit it. */
  advance: (id: string, body: AdvanceRequest) =>
    post<AdvanceResponse>(`/api/session/${id}/advance`, body),

  /**
   * End a playthrough. Not a delete — the session is marked terminated and its state stays on
   * disk (DEPLOY.md: "session data, not just logs"). The clerk's third trigger is a closing
   * session, so this is also what makes the last unfiled stretch reach the file.
   */
  close: (id: string) => post<void>(`/api/session/${id}/close`),
  /**
   * Erase a playthrough everywhere it lives: the store, the mirror, the turn logs. The receipt's
   * numbers survive. This is what the start-over control calls, so its words are true (2026-09-14).
   */
  forget: (id: string) => post<void>(`/api/session/${id}/forget`),
  pause: (id: string) => post<void>(`/api/session/${id}/pause`),
  resume: (id: string) => post<void>(`/api/session/${id}/resume`),
  hold: (id: string, body?: unknown) => post<void>(`/api/session/${id}/hold`, body),
  activity: (id: string, body?: unknown) =>
    post<void>(`/api/session/${id}/activity`, body),

  // --- reads -------------------------------------------------------------
  getAffordances: (id: string) =>
    request<AffordancesResponse>(`/api/session/${id}/affordances`),

  /** The emitted history, oldest first. The only source of backlog. */
  getMessages: (id: string, since?: string) =>
    request<MessagesResponse>(
      `/api/session/${id}/messages${since ? `?since=${encodeURIComponent(since)}` : ''}`,
    ),

  getTurns: (id: string) => request<unknown[]>(`/api/session/${id}/turns`),

  // --- packs -------------------------------------------------------------
  /** The pack inventory. Carries per-pack settings the surfaces need (e.g. restMorningHour). */
  listPacks: () => request<PackListResponse>('/api/packs'),

  getPackBriefing: (packId: string) =>
    request<unknown>(`/api/packs/${packId}/briefing`),

  getPackOnboarding: (packId: string) =>
    request<unknown>(`/api/packs/${packId}/onboarding`),

  getPackAssets: (packId: string) =>
    request<unknown[]>(`/api/packs/${packId}/assets-manifest`),

  // --- account -----------------------------------------------------------
  /** Who the server says you are. The single source of truth for access. */
  me: () => request<Account>('/api/me'),

  // --- admin -------------------------------------------------------------
  /** Every account. Admin only, enforced server-side. */
  listAccounts: () => request<AccountRow[]>('/api/admin/accounts'),

  /** Let someone play (or take it back). Admin only, enforced server-side. */
  setApproval: (id: string, approved: boolean) =>
    post<{ ok: boolean }>(`/api/admin/accounts/${id}/approval`, { approved }),

  // --- reading sittings back ---------------------------------------------
  /*
   * ⚠ THE SERVER DECIDES WHAT IS READABLE, NOT THIS FILE. A pack whose transcript posture is
   * `unread` is absent from `listSittings` and 404s from `readSitting` — the client never sees it
   * and therefore cannot leak it by rendering carelessly. See `ITranscripts`.
   */
  /** Every pack that has produced a sitting, and the posture over it — unread ones included. */
  transcriptPacks: () =>
    request<{ packs: TranscriptPack[] }>('/api/admin/transcripts/packs'),

  /** Readable sittings, newest first. */
  listSittings: (packId?: string) =>
    request<{ sittings: Sitting[] }>(
      `/api/admin/transcripts${packId ? `?packId=${encodeURIComponent(packId)}` : ''}`,
    ),

  /** One sitting's lines. 404 when it does not exist OR its pack is not readable. */
  readSitting: (sessionId: string) =>
    request<{ sitting: Sitting; lines: TranscriptLine[] }>(
      `/api/admin/transcripts/${sessionId}`,
    ),

  /**
   * Decide whether a pack's sittings may be read.
   *
   * ⚠ `note` is REQUIRED for 'readable' and refused-without server-side — opening a door onto
   * conversations held in confidence costs a sentence saying why. Closing one costs nothing.
   */
  setPosture: (packId: string, posture: 'readable' | 'unread', note?: string) =>
    post<{ packId: string; posture: string }>('/api/admin/transcripts/posture', {
      packId,
      posture,
      note,
    }),
}
